import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Banner,
  Cell,
  List,
  Placeholder,
  Section,
  Spinner,
  Switch,
} from "@telegram-apps/telegram-ui";

import { api } from "../api";
import { useAppData } from "../AppData";
import { useBackButton } from "../hooks/useBackButton";
import { useMainButton } from "../hooks/useMainButton";
import {
  REQUEST_FEATURE,
  type OperationCategory,
  type UserInfo,
  type UserRole,
} from "../types";

const CATEGORY_LABELS: Record<OperationCategory, string> = {
  calc: "Подсчёт",
  views: "Просмотры",
  report: "Отчёт",
};

const CATEGORY_ORDER: OperationCategory[] = ["calc", "views", "report"];

function displayName(u: UserInfo): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (u.username) return `@${u.username}`;
  return `ID ${u.tgid}`;
}

export function UserDetailPage() {
  const { tgid: tgidParam } = useParams<{ tgid: string }>();
  const tgid = Number(tgidParam);
  const navigate = useNavigate();
  const { me, operations, loading: meLoading, reload } = useAppData();

  const [user, setUser] = useState<UserInfo | null>(null);
  const [role, setRole] = useState<UserRole>("user");
  const [perms, setPerms] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useBackButton(true, () => navigate("/settings/users"));

  useEffect(() => {
    if (!me?.is_admin) return;
    api
      .getUser(tgid)
      .then((u) => {
        setUser(u);
        setRole(u.role);
        setPerms(new Set(u.permissions));
      })
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : String(e)),
      );
  }, [tgid, me]);

  const isSelf = me?.tgid === tgid;
  const isAdminRole = role === "admin";

  const togglePerm = (opId: string, on: boolean) => {
    setPerms((prev) => {
      const next = new Set(prev);
      if (on) next.add(opId);
      else next.delete(opId);
      return next;
    });
  };

  const save = useMemo(
    () => async () => {
      if (saving) return;
      setSaving(true);
      setSaveError(null);
      try {
        await api.updateUser(tgid, {
          role,
          permissions: Array.from(perms),
        });
        reload();
        navigate("/settings/users");
      } catch (e: unknown) {
        setSaveError(e instanceof Error ? e.message : String(e));
        setSaving(false);
      }
    },
    [tgid, role, perms, saving, reload, navigate],
  );

  useMainButton({
    text: saving ? "Сохраняю..." : "Сохранить",
    visible: !!user,
    enabled: !saving,
    loading: saving,
    onClick: save,
  });

  if (meLoading) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spinner size="m" />
      </div>
    );
  }

  if (me && !me.is_admin) {
    return <Navigate to="/settings" replace />;
  }

  if (loadError) {
    return <Placeholder header="Ошибка" description={loadError} />;
  }
  if (!user) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spinner size="m" />
      </div>
    );
  }

  return (
    <div className="page">
      <List>
        <Section header="Пользователь">
          <Cell subtitle={user.username ? `@${user.username} · ID ${user.tgid}` : `ID ${user.tgid}`}>
            {displayName(user)}
          </Cell>
        </Section>

        <Section
          header="Роль"
          footer={
            isAdminRole
              ? "Администратор имеет полный доступ ко всем функциям."
              : "Обычный пользователь видит только отмеченные ниже функции."
          }
        >
          <Cell
            Component="label"
            after={
              <Switch
                checked={isAdminRole}
                disabled={isSelf}
                onChange={(e) => setRole(e.target.checked ? "admin" : "user")}
              />
            }
            subtitle={isSelf ? "Нельзя изменить собственную роль" : undefined}
          >
            Администратор
          </Cell>
        </Section>

        {!isAdminRole && (
          <Section
            header="Доступ · Заявка"
            footer="Создание заявок: выбор контрагента, подсчёт суммы и формирование текста."
          >
            <Cell
              Component="label"
              after={
                <Switch
                  checked={perms.has(REQUEST_FEATURE)}
                  onChange={(e) => togglePerm(REQUEST_FEATURE, e.target.checked)}
                />
              }
            >
              Заявка
            </Cell>
          </Section>
        )}

        {!isAdminRole &&
          CATEGORY_ORDER.map((cat) => {
            const ops = operations.filter((o) => o.category === cat);
            if (ops.length === 0) return null;
            return (
              <Section key={cat} header={`Доступ · ${CATEGORY_LABELS[cat]}`}>
                {ops.map((op) => (
                  <Cell
                    key={op.id}
                    Component="label"
                    multiline
                    subtitle={op.description}
                    after={
                      <Switch
                        checked={perms.has(op.id)}
                        onChange={(e) => togglePerm(op.id, e.target.checked)}
                      />
                    }
                  >
                    {op.title}
                  </Cell>
                ))}
              </Section>
            );
          })}

        {saveError && (
          <div style={{ padding: "8px 16px" }}>
            <Banner type="section" header="Не удалось сохранить" description={saveError} />
          </div>
        )}
      </List>
    </div>
  );
}
